import { useState, useMemo } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Select,
  Tooltip,
  Paper,
  TextInput,
  Table,
  Pagination,
  ActionIcon,
  Loader,
  Center,
  Pill,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import { useForm } from "@mantine/form";
import {
  IconSearch,
  IconPlus,
  IconTrash,
  IconFilter,
  IconRefresh,
} from "@tabler/icons-react";
import {
  useRolePermissions,
  useCreateRolePermission,
  useDeleteRolePermission,
} from "../../../hooks/admin/useRolePermissions";
import { useRolesAdmin } from "../../../hooks/admin/useRoles";
import { usePermissionsAdmin } from "../../../hooks/admin/usePermissions";
import { RolePermission } from "../../../services/admin/rolepermissions.service";
import { required, scrollToFirstError } from "../../../utils/formValidation";
import { formatPermissionLabel } from "../../../utils/formatters";

const ITEMS_PER_PAGE = 20;

interface FormValues {
  roleId: string;
  permissionId: string;
}

interface RPFilters {
  roleId?: string | null;
  permissionId?: string | null;
}

export default function RolePermissionsAdminPage() {
  // Modal states
  const [formModalOpened, { open: openFormModal, close: closeFormModal }] =
    useDisclosure(false);
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);
  const [
    confirmModalOpened,
    { open: openConfirmModal, close: closeConfirmModal },
  ] = useDisclosure(false);

  // Page state
  const [rpToDelete, setRpToDelete] = useState<RolePermission | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [filters, setFilters] = useState<RPFilters>({});
  const [tempFilters, setTempFilters] = useState<RPFilters>({});
  const [page, setPage] = useState(1);

  // Data hooks
  const {
    data: rolePermissions = [],
    isLoading,
    refetch,
  } = useRolePermissions();
  const { data: roles = [] } = useRolesAdmin();
  const { data: permissions = [] } = usePermissionsAdmin();
  const createRolePermission = useCreateRolePermission();
  const deleteRolePermission = useDeleteRolePermission();

  // Form
  const form = useForm<FormValues>({
    initialValues: {
      roleId: "",
      permissionId: "",
    },
    validate: {
      roleId: required("Role is required"),
      permissionId: required("Permission is required"),
    },
  });

  // Filter by search and filters
  const filteredRolePermissions = useMemo(() => {
    let result = rolePermissions;

    // Filter by role
    if (filters.roleId) {
      result = result.filter((rp) => rp.roleId === filters.roleId);
    }

    // Filter by permission
    if (filters.permissionId) {
      result = result.filter((rp) => rp.permissionId === filters.permissionId);
    }

    // Filter by search text
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      result = result.filter(
        (rp) =>
          rp.role?.name.toLowerCase().includes(searchLower) ||
          rp.permission?.name.toLowerCase().includes(searchLower) ||
          rp.permission?.description?.toLowerCase().includes(searchLower)
      );
    }

    return result;
  }, [rolePermissions, debouncedSearch, filters]);

  // Check if any filters are active
  const hasActiveFilters = !!filters.roleId || !!filters.permissionId;

  // Pagination
  const totalPages = Math.ceil(filteredRolePermissions.length / ITEMS_PER_PAGE);
  const paginatedRPs = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredRolePermissions.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredRolePermissions, page]);

  // Options for form dropdowns (only active roles)
  const roleOptions = roles
    .filter((r) => r.isActive)
    .map((r) => ({ value: r.id, label: r.name }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const permissionOptions = permissions
    .map((p) => ({
      value: p.id,
      label: formatPermissionLabel(p),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // Options for filter dropdowns (all roles, shorter labels)
  const filterRoleOptions = roles
    .map((r) => ({ value: r.id, label: r.name }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const filterPermissionOptions = permissions
    .map((p) => ({ value: p.id, label: formatPermissionLabel(p) }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // Handlers
  const handleAdd = () => {
    form.reset();
    openFormModal();
  };

  const handleSubmit = form.onSubmit(
    async (values) => {
      await createRolePermission.mutateAsync(values);
      closeFormModal();
      form.reset();
    },
    () => scrollToFirstError(form)
  );

  const handleDeleteClick = (rp: RolePermission) => {
    setRpToDelete(rp);
    openConfirmModal();
  };

  const confirmDelete = async () => {
    if (rpToDelete) {
      await deleteRolePermission.mutateAsync(rpToDelete.id);
      closeConfirmModal();
      setRpToDelete(null);
    }
  };

  const handleOpenFilterModal = () => {
    setTempFilters({ ...filters });
    openFilterModal();
  };

  const handleApplyFilters = () => {
    setFilters({ ...tempFilters });
    setPage(1);
    closeFilterModal();
  };

  const handleClearAllFilters = () => {
    setFilters({});
    setPage(1);
  };

  const handleRemoveFilter = (key: keyof RPFilters) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[key];
      return newFilters;
    });
    setPage(1);
  };

  // Get display names for active filters
  const getFilterDisplayName = (key: keyof RPFilters): string | null => {
    if (key === "roleId" && filters.roleId) {
      return roles.find((r) => r.id === filters.roleId)?.name || null;
    }
    if (key === "permissionId" && filters.permissionId) {
      const perm = permissions.find((p) => p.id === filters.permissionId);
      return perm ? formatPermissionLabel(perm) : null;
    }
    return null;
  };

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Role Permissions</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Manage which permissions are assigned to each role
              </Text>
            </div>
            <Button leftSection={<IconPlus size={16} />} onClick={handleAdd}>
              Add New
            </Button>
          </Group>

          {/* Search and Filters Row */}
          <Group align="stretch" grow>
            {/* Search Section */}
            <Paper p="md" withBorder style={{ flex: 2 }}>
              <Stack gap="sm">
                <Group>
                  <IconSearch size={20} />
                  <Text fw={500}>Search</Text>
                </Group>
                <TextInput
                  placeholder="Search by role or permission name..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  leftSection={<IconSearch size={16} />}
                />
              </Stack>
            </Paper>

            {/* Filters Section */}
            <Paper p="md" withBorder style={{ flex: 1 }}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group>
                    <IconFilter size={20} />
                    <Text fw={500}>Filters</Text>
                  </Group>
                  <Button
                    variant="subtle"
                    size="compact-sm"
                    onClick={() => refetch()}
                    loading={isLoading}
                    leftSection={<IconRefresh size={16} />}
                  />
                </Group>
                <Button
                  variant="light"
                  onClick={handleOpenFilterModal}
                  fullWidth
                >
                  {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                </Button>
                {hasActiveFilters && (
                  <Group gap="xs" align="center">
                    <Text size="sm" c="dimmed">
                      Filters:
                    </Text>
                    {filters.roleId && (
                      <Pill
                        withRemoveButton
                        onRemove={() => handleRemoveFilter("roleId")}
                      >
                        {getFilterDisplayName("roleId")}
                      </Pill>
                    )}
                    {filters.permissionId && (
                      <Pill
                        withRemoveButton
                        onRemove={() => handleRemoveFilter("permissionId")}
                      >
                        {getFilterDisplayName("permissionId")}
                      </Pill>
                    )}
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      color="gray"
                      onClick={handleClearAllFilters}
                    >
                      Clear All
                    </Button>
                  </Group>
                )}
                <Text size="sm" c="dimmed">
                  Showing {filteredRolePermissions.length} of{" "}
                  {rolePermissions.length} role permissions
                </Text>
              </Stack>
            </Paper>
          </Group>

          {/* Table Section */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              {/* Table Header with Pagination */}
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  Role Permissions ({filteredRolePermissions.length})
                </Text>
                {totalPages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {(page - 1) * ITEMS_PER_PAGE + 1}-
                      {Math.min(
                        page * ITEMS_PER_PAGE,
                        filteredRolePermissions.length
                      )}{" "}
                      of {filteredRolePermissions.length}
                    </Text>
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {/* Table */}
              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : filteredRolePermissions.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {search || hasActiveFilters
                    ? "No role permissions match the current filters"
                    : "No role permissions found"}
                </Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Role</Table.Th>
                      <Table.Th>Permission</Table.Th>
                      <Table.Th>Added</Table.Th>
                      <Table.Th style={{ width: 80 }}>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedRPs.map((rp) => (
                      <Table.Tr key={rp.id}>
                        {/* Role */}
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {rp.role?.name || "—"}
                          </Text>
                        </Table.Td>

                        {/* Permission */}
                        <Table.Td>
                          <div>
                            <Text size="sm" fw={500}>
                              {formatPermissionLabel(rp.permission)}
                            </Text>
                            <Text size="xs" c="dimmed" lineClamp={1}>
                              {rp.permission?.name || ""}
                            </Text>
                          </div>
                        </Table.Td>

                        {/* Added */}
                        <Table.Td>
                          <Text size="sm">
                            {new Date(rp.createdAt).toLocaleDateString()}
                          </Text>
                        </Table.Td>

                        {/* Actions */}
                        <Table.Td>
                          <Tooltip label="Remove">
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              onClick={() => handleDeleteClick(rp)}
                            >
                              <IconTrash size={18} />
                            </ActionIcon>
                          </Tooltip>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>

        {/* Add Permission to Role Modal */}
        <Modal
          opened={formModalOpened}
          onClose={closeFormModal}
          title="Add Permission to Role"
          size="md"
          closeOnClickOutside={false}
        >
          <form onSubmit={handleSubmit}>
            <Stack gap="md">
              <Select
                label="Role"
                placeholder="Select a role"
                data={roleOptions}
                searchable
                withAsterisk
                {...form.getInputProps("roleId")}
              />

              <Select
                label="Permission"
                placeholder="Select a permission"
                data={permissionOptions}
                searchable
                withAsterisk
                {...form.getInputProps("permissionId")}
              />

              <Group justify="flex-end" mt="md">
                <Button variant="subtle" onClick={closeFormModal}>
                  Cancel
                </Button>
                <Button type="submit" loading={createRolePermission.isPending}>
                  Add Permission
                </Button>
              </Group>
            </Stack>
          </form>
        </Modal>

        {/* Filter Modal */}
        <Modal
          opened={filterModalOpened}
          onClose={closeFilterModal}
          title="Filter Role Permissions"
          size="sm"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <Select
              label="Role"
              placeholder="All roles"
              data={filterRoleOptions}
              value={tempFilters.roleId || null}
              onChange={(value) =>
                setTempFilters((prev) => ({ ...prev, roleId: value }))
              }
              clearable
              searchable
            />

            <Select
              label="Permission"
              placeholder="All permissions"
              data={filterPermissionOptions}
              value={tempFilters.permissionId || null}
              onChange={(value) =>
                setTempFilters((prev) => ({ ...prev, permissionId: value }))
              }
              clearable
              searchable
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeFilterModal}>
                Cancel
              </Button>
              <Button onClick={handleApplyFilters}>Apply Filters</Button>
            </Group>
          </Stack>
        </Modal>

        {/* Confirmation Modal for Delete */}
        <Modal
          opened={confirmModalOpened}
          onClose={closeConfirmModal}
          title="Remove Permission"
          size="sm"
          centered
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to remove the permission{" "}
              <strong>{formatPermissionLabel(rpToDelete?.permission)}</strong>{" "}
              from role <strong>{rpToDelete?.role?.name}</strong>?
            </Text>
            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeConfirmModal}>
                Cancel
              </Button>
              <Button
                color="red"
                onClick={confirmDelete}
                loading={deleteRolePermission.isPending}
              >
                Remove
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Box>
    </Container>
  );
}
